/**
 * NotificationBell — V2 Notification System (Enterprise Circle Invites)
 *
 * Architecture:
 * - DB-backed notifications (table: public.notifications) via React Query
 * - Attendance confirmations merged from AppContext markedDays (in-memory)
 *
 * Smart Polling:
 * - Inbox: refetchInterval 5 min + refetchOnWindowFocus
 * - History: lazy — only fetched when the History tab is opened
 *
 * Security:
 * - Workspace invite acceptance goes through the `accept-invite` Edge Function.
 * - Circle invite acceptance goes through `resolve_buddy_invite` RPC.
 *
 * This file is now a thin orchestrator — see `src/features/notifications/`
 * for the extracted panels, hooks, and presentational components.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useLiveToday } from "@/features/calendar/hooks/useLiveToday";
import { useTodayReminderPassed } from "@/features/calendar/hooks/useTodayReminderPassed";
import {
  useBreakfastMutations,
  usePendingBreakfastInvites,
} from "@/hooks/useBreakfastMutations";

import { extractEventId } from "@/features/notifications/lib/format";
import { getCoveredDailyReminderIds } from "@/features/notifications/lib/dailyReminderDedupe";
import { getNotificationBadgeCount } from "@/features/notifications/lib/notificationCounts";
import type { DBNotification } from "@/features/notifications/types";
import { useInbox, useHistory } from "@/features/notifications/hooks/useNotifQueries";
import { useNotificationRealtime } from "@/features/notifications/hooks/useNotificationRealtime";
import { useNotifMutations } from "@/features/notifications/hooks/useNotifMutations";
import { useNotifSenders } from "@/features/notifications/hooks/useNotifSenders";
import { useWorkspaceInviteActions } from "@/features/notifications/hooks/useWorkspaceInviteActions";
import { useCircleInviteActions } from "@/features/notifications/hooks/useCircleInviteActions";
import { useBreakfastNotifActions } from "@/features/notifications/hooks/useBreakfastNotifActions";
import { useEventCancellationActions } from "@/features/notifications/hooks/useEventCancellationActions";
import { useAttendanceActions } from "@/features/notifications/hooks/useAttendanceActions";
import { NotificationPopover } from "@/features/notifications/components/NotificationPopover";
import { NotificationSheet } from "@/features/notifications/components/NotificationSheet";
import type {
  NotifPanelActions,
  NotifPanelLoading,
} from "@/features/notifications/components/panel-types";

export function NotificationBell() {
  // ── UI state ─────────────────────────────────────────────────
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [sheetOpen,   setSheetOpen]   = useState(false);
  const [activeTab,   setActiveTab]   = useState<"inbox" | "history">("inbox");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hasRung,     setHasRung]     = useState(false);
  // `highlightId` is the notification the sheet should scroll to + pulse
  // once it opens. Set by the popover when the user clicks a preview
  // row; cleared a moment later so the ring animation only plays once.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [inboxClearedFocusSignal, setInboxClearedFocusSignal] = useState(0);
  const prevBadgeRef = useRef(0);

  // ── Data sources ─────────────────────────────────────────────
  const { profile, markedDays, dayTypes, subscribedHolidays, notifications } = useApp();
  const todayStr = useLiveToday();
  // The reminder/alarm time is the trigger: a still-unconfirmed office day
  // only becomes confirmable once its own alarm has fired today.
  const todayReminderPassed = useTodayReminderPassed(notifications?.reminderTime);

  const { data: inbox   = [] } = useInbox();
  const { data: history = [] } = useHistory(sheetOpen && activeTab === "history");

  // Batch-resolve every distinct `sender_id` across inbox + history into
  // a single profiles query. Falls back to an empty Map while loading,
  // which `NotifAvatar` handles gracefully (themed icon / initials).
  const senders = useNotifSenders(useMemo(() => [...inbox, ...history], [inbox, history]));

  // Attendance lane — derive the "confirm?" cards right here; it's a
  // trivial useMemo over AppContext state and doesn't warrant its own hook.
  const daysToConfirm = useMemo(() => {
    const officeMatch = (typeId: string) => {
      const type = dayTypes.find((t) => t.id === typeId);
      return (
        type?.baseCategory === "WORK_OFFICE" ||
        type?.baseCategory === "WORK_EXTERNAL" ||
        type?.id === "office" ||
        type?.id === "business_travel" ||
        type?.id === "client_visit"
      );
    };
    return markedDays
      .filter((d) => {
        if (!officeMatch(d.typeId) || d.status !== "unconfirmed") return false;
        // Never nag on an imported (ICS) holiday — the user is off that day.
        if (subscribedHolidays?.[d.date]) return false;
        // Only prompt once the day's own reminder/alarm time has fired:
        // any past day, or today after the alarm. Future days stay Planned.
        return d.date < todayStr || (d.date === todayStr && todayReminderPassed);
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [markedDays, dayTypes, todayStr, todayReminderPassed, subscribedHolidays]);

  // Breakfast lane — sourced from breakfast_attendees, not notifications.
  // Counted toward the bell badge even when no `BREAKFAST_INVITE`
  // notification row exists — belt-and-braces in case the upstream notif
  // insert was dropped (e.g. direct-table invitee sync path). The bell
  // should always reflect the DB truth for RSVPs the user owes a response on.
  const { data: pendingInvites = [] } = usePendingBreakfastInvites(profile.userId);
  const { setMyRsvp } = useBreakfastMutations();

  // ── Mutation bundles ─────────────────────────────────────────
  const { markRead, archive, unarchive, deleteOne, clearAllInbox, bulkDelete } =
    useNotifMutations(inbox);
  const { acceptInvite, declineInvite }  = useWorkspaceInviteActions();
  const { resolveBuddyInvite }           = useCircleInviteActions();
  const { breakfastRsvp }                = useBreakfastNotifActions();
  const { removeFromCalendar }           = useEventCancellationActions();
  const { validateAttendance, clearAttendance } = useAttendanceActions();

  // ── Realtime subscription ────────────────────────────────────
  useNotificationRealtime(profile.userId);

  const { rescheduledEventIds, coveredNotifIds } = useMemo(() => {
    // Breakfast + daily-reminder rows hidden when a first-class card
    // already surfaces the same actionable item.
    const pendingIds  = new Set(pendingInvites.map((i) => i.id));
    const rescheduled = new Set<string>();
    const covered     = new Set<string>();
    for (const n of inbox) {
      if (n.type !== "BREAKFAST_INVITE" && n.type !== "EVENT_UPDATE") continue;
      const eventId = extractEventId(n.action_url);
      if (!eventId || !pendingIds.has(eventId)) continue;
      covered.add(n.id);
      if (n.type === "EVENT_UPDATE") rescheduled.add(eventId);
    }
    for (const id of getCoveredDailyReminderIds(inbox, daysToConfirm, markedDays, todayStr)) {
      covered.add(id);
    }
    return { rescheduledEventIds: rescheduled, coveredNotifIds: covered };
  }, [inbox, pendingInvites, daysToConfirm, markedDays, todayStr]);

  // ── Derived counts (deduped — matches what the UI actually shows) ─
  const pendingInviteCount = pendingInvites.length;
  const badgeCount         = getNotificationBadgeCount(
    inbox,
    coveredNotifIds,
    daysToConfirm.length,
    pendingInviteCount,
  );
  const hasPending         = badgeCount > 0;

  // Auto-archive DB rows that are hidden by a first-class card so they
  // don't linger as unread ghosts inflating the badge.
  const resolvingCoveredRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const stale = inbox.filter(
      (n) => coveredNotifIds.has(n.id) && !resolvingCoveredRef.current.has(n.id),
    );
    if (stale.length === 0) return;
    for (const n of stale) {
      resolvingCoveredRef.current.add(n.id);
      archive.mutate(n.id);
    }
  }, [inbox, coveredNotifIds, archive]);

  // ── Bell swing on new notifications ──────────────────────────
  useEffect(() => {
    if (badgeCount > prevBadgeRef.current) {
      setHasRung(false);
      requestAnimationFrame(() => setHasRung(true));
    }
    prevBadgeRef.current = badgeCount;
  }, [badgeCount]);

  // ── Scroll lock when sheet is open ───────────────────────────
  useEffect(() => {
    document.body.style.overflow = sheetOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sheetOpen]);

  // ── Clear selection when switching tabs ──────────────────────
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  // ── Popover top-4 budgeting ──────────────────────────────────
  const { popAtt, popDb, popInvites } = useMemo(() => {
    // Budget MAX slots across the three lanes, prioritising breakfast invites
    // (they block a scheduled event from showing on the grid) then attendance
    // confirmations, then unread DB notifications that aren't already
    // covered by an invite card above.
    const MAX          = 4;
    const inviteSlots  = Math.min(pendingInvites.length, MAX);
    const attSlots     = Math.min(daysToConfirm.length, MAX - inviteSlots);
    const dbSlots      = MAX - inviteSlots - attSlots;
    return {
      popInvites: pendingInvites.slice(0, inviteSlots),
      popAtt:     daysToConfirm.slice(0, attSlots),
      popDb:      inbox
        .filter((n) => !n.is_read && !coveredNotifIds.has(n.id))
        .slice(0, dbSlots),
    };
  }, [daysToConfirm, inbox, pendingInvites, coveredNotifIds]);

  // ── Handlers ─────────────────────────────────────────────────
  const handleOpenSheet = useCallback(
    (tab: "inbox" | "history" = "inbox", notifId?: string) => {
      setActiveTab(tab);
      setPopoverOpen(false);
      if (notifId) setHighlightId(notifId);
      // Defer mount so Radix can finish closing the popover without a
      // focus-trap conflict, then clear the highlight after the ring
      // animation finishes (~1.8s) so a subsequent reopen doesn't
      // re-play it spuriously.
      setTimeout(() => setSheetOpen(true), 80);
    },
    [],
  );

  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 2_000);
    return () => clearTimeout(t);
  }, [highlightId]);

  // Reset highlight whenever the sheet closes so re-opening always
  // starts clean.
  useEffect(() => {
    if (!sheetOpen) setHighlightId(null);
  }, [sheetOpen]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedIds(new Set(history.map((n) => n.id)));
  }, [history]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBulkDelete = useCallback(
    (ids: string[]) => {
      bulkDelete.mutate(ids, {
        onSuccess: () => setSelectedIds(new Set()),
      });
    },
    [bulkDelete],
  );

  // ── Loading state for card-level UX ──────────────────────────
  //
  // Replaces the previous `loadingAttId` / dateStr tracking with pure
  // derivations from each mutation's `.variables` + `.isPending` — no
  // extra useState needed. Same rule for the breakfast-invite cards in
  // both the popover preview and the sheet inbox.
  const attendanceLoadingDate: string | null =
    (validateAttendance.isPending && (validateAttendance.variables ?? null)) ||
    (clearAttendance.isPending    && (clearAttendance.variables ?? null))    ||
    null;

  const breakfastInviteLoadingId: string | null =
    (setMyRsvp.isPending && (setMyRsvp.variables?.eventId ?? null)) || null;

  const loading: NotifPanelLoading = {
    attendanceDate:         attendanceLoadingDate,
    breakfastInviteEventId: breakfastInviteLoadingId,
  };

  const actions: NotifPanelActions = {
    onMarkRead:   (id) => markRead.mutate(id),
    onArchive:    (id) => archive.mutate(id),
    onDelete:     (id, fromHistory) => deleteOne.mutate({ id, fromHistory }),
    onUnarchive:  (id) => unarchive.mutate(id),

    onAcceptWorkspaceInvite:  (notif: DBNotification) => acceptInvite.mutate(notif),
    onDeclineWorkspaceInvite: (notif: DBNotification) => declineInvite.mutate(notif),
    onAcceptBuddy:            (notif: DBNotification) => resolveBuddyInvite.mutate({ notif, accept: true  }),
    onDeclineBuddy:           (notif: DBNotification) => resolveBuddyInvite.mutate({ notif, accept: false }),
    onAcceptBreakfast:        (notif: DBNotification) => breakfastRsvp.mutate({ notif, accept: true  }),
    onDeclineBreakfast:       (notif: DBNotification) => breakfastRsvp.mutate({ notif, accept: false }),
    onRemoveFromCalendar:     (notif: DBNotification) => removeFromCalendar.mutate({ notif }),

    onValidateAttendance: (dateStr) => validateAttendance.mutate(dateStr),
    onClearAttendance:    (dateStr) => clearAttendance.mutate(dateStr),

    onAcceptBreakfastInvite:  (eventId) => setMyRsvp.mutate({ eventId, status: "confirmed" }),
    onDeclineBreakfastInvite: (eventId) => setMyRsvp.mutate({ eventId, status: "declined"  }),
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes bell-swing {
          0%   { transform: rotate(0deg);   }
          15%  { transform: rotate(18deg);  }
          30%  { transform: rotate(-14deg); }
          45%  { transform: rotate(10deg);  }
          60%  { transform: rotate(-6deg);  }
          75%  { transform: rotate(3deg);   }
          100% { transform: rotate(0deg);   }
        }
        .bell-swing { animation: bell-swing 0.7s ease-in-out; }
      `}</style>

      <NotificationPopover
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        hasPending={hasPending}
        badgeCount={badgeCount}
        hasRung={hasRung}
        onBellSwingEnd={() => setHasRung(false)}
        onOpenFullSheet={(notifId) => handleOpenSheet("inbox", notifId)}
        popInvites={popInvites}
        popAtt={popAtt}
        popDb={popDb}
        rescheduledEventIds={rescheduledEventIds}
        senders={senders}
        actions={actions}
        loading={loading}
      />

      <NotificationSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        badgeCount={badgeCount}
        inbox={inbox}
        daysToConfirm={daysToConfirm}
        pendingInvites={pendingInvites}
        rescheduledEventIds={rescheduledEventIds}
        coveredNotifIds={coveredNotifIds}
        isClearingAllInbox={clearAllInbox.isPending}
        onClearAllInbox={() => {
          clearAllInbox.mutate(undefined, {
            onSuccess: () => setInboxClearedFocusSignal((n) => n + 1),
          });
        }}
        inboxClearedFocusSignal={inboxClearedFocusSignal}
        history={history}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        isBulkDeleting={bulkDelete.isPending}
        onBulkDelete={handleBulkDelete}
        senders={senders}
        highlightId={highlightId}
        actions={actions}
        loading={loading}
      />
    </>
  );
}
